// Component Imports
import AuthRedirect from '@/components/AuthRedirect'
import { Auth } from '@data/users'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { fetchUser } from '@/redux-store/slices/user'
import type { RootState } from '@/redux-store'
import { useAppDispatch } from '@/redux-store'
import { useLocation, useNavigate, Navigate } from 'react-router-dom'
import { RoleIdEnum, UserTypeEnum } from '@/@data'

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const [isValid, setIsValid] = useState(true)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { user, loading } = useSelector((state: RootState) => state.userReducer)

  // Check if route is restricted for USER_EMPLOYER
  const isRestrictedRouteForEmployer = React.useMemo(() => {
    if (!user || user.role?.id !== RoleIdEnum.USER_EMPLOYER) {
      return false
    }
    
    if (!pathname.startsWith('/cms')) {
      return false
    }
    
    const allowedRoutes = [
      `/cms/finance/balance/${user.company_id}`,
      `/cms/finance/balance/${user.company_id}/transaction/`,
      `/cms/users/profile/`
    ]
    
    return !allowedRoutes.some(route => pathname.startsWith(route))
  }, [user, pathname])

  // Check if route is restricted for FINANCE role
  const isRestrictedRouteForFinance = React.useMemo(() => {
    if (!user || user.role?.id !== RoleIdEnum.FINANCE) {
      return false
    }
    
    if (!pathname.startsWith('/cms')) {
      return false
    }
    
    const allowedRoutes = [
      `/cms/attendance`,
      `/cms/finance/transaction`,
      `/cms/users/profile/`
    ]
    
    return !allowedRoutes.some(route => pathname.startsWith(route))
  }, [user, pathname])

  // Check if route is restricted for HR role
  const isRestrictedRouteForHR = React.useMemo(() => {
    if (!user || user.role?.id !== RoleIdEnum.HR) {
      return false
    }
    
    if (!pathname.startsWith('/cms')) {
      return false
    }
    
    const allowedRoutes = [
      `/cms/users/employee`,
      `/cms/employer`,
      `/cms/attendance`,
      `/cms/users/profile/`
    ]
    
    return !allowedRoutes.some(route => pathname.startsWith(route))
  }, [user, pathname])

  useEffect(() => {
    const valid = Auth.checkValidToken()

    setIsValid(!!valid)

    if (valid && !user) {
      dispatch(fetchUser())
    }

    const timer = setTimeout(() => {
      setIsFirstLoad(false)
    }, 1000)

    return () => clearTimeout(timer)

  }, [dispatch, user])

  useEffect(() => {
    if (!isFirstLoad && !loading && user) {
      // Don't redirect if on landing pages (not under /cms)
      if (!pathname.startsWith('/cms')) {
        return
      }

      // Check if user is USER_EMPLOYER role (id = 4)
      if (user.role?.id === RoleIdEnum.USER_EMPLOYER) {
        // Define allowed routes for USER_EMPLOYER
        const allowedRoutes = [
          `/cms/finance/balance/${user.company_id}`,
          `/cms/finance/balance/${user.company_id}/transaction/`,
          `/cms/users/profile/`
        ]

        // Check if current path is allowed
        const isAllowedRoute = allowedRoutes.some(route => pathname.startsWith(route))

        // If trying to access a restricted route, redirect to allowed route
        if (!isAllowedRoute) {
          navigate(`/cms/finance/balance/${user.company_id}`, { replace: true })
          return
        }
      }

      // Check if user is FINANCE role
      if (user.role?.id === RoleIdEnum.FINANCE) {
        // Define allowed routes for FINANCE
        const allowedRoutes = [
          `/cms/attendance`,
          `/cms/finance/transaction`,
          `/cms/users/profile/`
        ]

        // Check if current path is allowed
        const isAllowedRoute = allowedRoutes.some(route => pathname.startsWith(route))

        // If trying to access a restricted route, redirect to attendance page
        if (!isAllowedRoute) {
          navigate('/cms/attendance', { replace: true })
          return
        }
      }

      // Check if user is HR role
      if (user.role?.id === RoleIdEnum.HR) {
        // Define allowed routes for HR
        const allowedRoutes = [
          `/cms/users/employee`,
          `/cms/employer`,
          `/cms/attendance`,
          `/cms/users/profile/`
        ]

        // Check if current path is allowed
        const isAllowedRoute = allowedRoutes.some(route => pathname.startsWith(route))

        // If trying to access a restricted route, redirect to employee page
        if (!isAllowedRoute) {
          navigate('/cms/users/employee', { replace: true })
          return
        }
      }

      // Handle redirect for /cms root path
      if (pathname === '/cms') {
        if (user.user_type === UserTypeEnum.Admin) {
          if (user.role?.id === RoleIdEnum.USER_EMPLOYER) {
            navigate(`/cms/finance/balance/${user.company_id}`, { replace: true })
            return
          }
          if (user.role?.id === RoleIdEnum.FINANCE) {
            navigate('/cms/attendance', { replace: true })
            return
          }
          if (user.role?.id === RoleIdEnum.HR) {
            navigate('/cms/users/employee', { replace: true })
            return
          }
          navigate('/cms/users/employee', { replace: true })
          return
        } else {
          navigate('/cms/users/profile/history', { replace: true })
          return
        }
      }

      const isMyPagePath = pathname.startsWith('/cms/users/profile/')

      if (isMyPagePath) {
        return
      }
    }
  }, [isFirstLoad, loading, user, pathname, navigate])

  if (loading || isFirstLoad) {
    return (
      <div className='fixed inset-0 flex items-center justify-center bg-white bg-opacity-80'>
        <div className='flex flex-col items-center'>
          <div className='w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin'></div>
          <p className='mt-4 text-gray-600 font-medium'>Loading...</p>
        </div>
      </div>
    )
  }

  // Block access to restricted routes for USER_EMPLOYER
  if (isRestrictedRouteForEmployer && user?.company_id) {
    return <Navigate to={`/cms/finance/balance/${user.company_id}`} replace />
  }

  // Block access to restricted routes for FINANCE
  if (isRestrictedRouteForFinance) {
    return <Navigate to='/cms/attendance' replace />
  }

  // Block access to restricted routes for HR
  if (isRestrictedRouteForHR) {
    return <Navigate to='/cms/users/employee' replace />
  }

  return <>{isValid ? children : <AuthRedirect />}</>
}
